/**
 * Mock oral with a trainer, the 7-day Pass and the learners' reviews (D-142), shared by the web
 * app and the phone. The server decides everything (slots, rules, prices: data); these helpers
 * only present its answer: slots grouped by day in the learner's time zone, the time left to move
 * or cancel, the stars of a review, the « estimate » of a mark.
 */

export interface OralSlotView {
  starts_at: string;
  trainer: string;
  trainer_name: string;
  local_date: string;
  local_time: string;
}

export interface OralDay {
  date: string;
  slots: OralSlotView[];
}

/** Slots grouped by day of the learner's zone (the server gives `local_date`), in order. */
export function slotsByDay(slots: readonly OralSlotView[]): OralDay[] {
  const days = new Map<string, OralSlotView[]>();
  for (const slot of [...slots].sort((a, b) => a.starts_at.localeCompare(b.starts_at))) {
    const list = days.get(slot.local_date) ?? [];
    list.push(slot);
    days.set(slot.local_date, list);
  }
  return [...days.entries()].map(([date, list]) => ({ date, slots: list }));
}

const localeOf = (language: string) => (language.startsWith("en") ? "en-GB" : "fr-FR");

/** A valid IANA zone or the agency's (Africa/Douala). */
export function safeZone(zone: string | null | undefined): string {
  if (!zone) return "Africa/Douala";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return zone;
  } catch {
    return "Africa/Douala";
  }
}

/** ISO date-time → { date: « lundi 12 octobre », time: « 10:20 » } in the given zone. */
export function zonedWhen(iso: string, zone: string, language: string): { date: string; time: string } {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return { date: iso, time: "" };
  const timeZone = safeZone(zone);
  const locale = localeOf(language);
  return {
    date: value.toLocaleDateString(locale, { timeZone, weekday: "long", day: "numeric", month: "long" }),
    time: value.toLocaleTimeString(locale, { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
  };
}

/** « 2026-10-12 » (a slot's local date) → « lundi 12 octobre » (no time-zone shift). */
export function dayLabel(localDate: string, language: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!match) return localDate;
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return value.toLocaleDateString(localeOf(language), { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" });
}

/** Hours left before the change limit (`change_hours_before`); negative once it is passed. */
export function hoursToChange(startsAt: string, changeHours: number, now: Date = new Date()): number {
  const start = new Date(startsAt).getTime();
  if (Number.isNaN(start)) return -1;
  return (start - changeHours * 3_600_000 - now.getTime()) / 3_600_000;
}

/** « 4 / 5 » as stars for screen readers and the eye: « ★★★★☆ ». */
export function stars(rating: number): string {
  const value = Math.max(0, Math.min(5, Math.round(rating)));
  return "★".repeat(value) + "☆".repeat(5 - value);
}

/** The speaking mark of a session as shown (always with the « estimate » notice by the app). */
export function oralMark(result: Record<string, unknown> | null | undefined): string {
  if (!result) return "";
  if (typeof result.band === "number") return `band ${result.band}`;
  if (typeof result.percent === "number") return `${Math.round(result.percent)} %`;
  if (typeof result.score === "number") {
    return typeof result.nclc === "number" || typeof result.nclc === "string" ? `${result.score}/20 · NCLC ${String(result.nclc)}` : `${result.score}/20`;
  }
  return "";
}

/** Minutes and seconds of a task of the official outline: « 3 min 30 s ». */
export function taskDuration(seconds: number, language: string): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  // « min » and « s » are the international symbols, the same in French and English
  void language;
  if (!minutes) return `${rest} s`;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}

/** The criteria marks of the grid kept within each criterion's range and step (or allowed points). */
export function clampMark(value: number, max: number, step: number | null, allowed: readonly number[] | null): number {
  if (allowed && allowed.length) {
    return allowed.reduce((best, p) => (Math.abs(p - value) < Math.abs(best - value) ? p : best), allowed[0] ?? 0);
  }
  const bounded = Math.max(0, Math.min(max, Number.isFinite(value) ? value : 0));
  const unit = step && step > 0 ? step : 1;
  return Math.round(bounded / unit) * unit;
}
